<template>
    <div class="flex flex-col gap-4 p-4 sm:p-6">
        <div class="flex flex-wrap items-center justify-between gap-2">
            <p class="text-sm text-muted">{{ $t('attachment.help') }}</p>

            <UButton
                :label="$t('attachment.upload')"
                icon="i-lucide-upload"
                :loading="uploading"
                @click="fileInput?.click()"
            />
            <input ref="fileInput" type="file" class="hidden" multiple @change="onPicked">
        </div>

        <div v-if="loading && !loaded" class="flex justify-center py-8">
            <UIcon name="i-lucide-loader-circle" class="size-5 animate-spin text-muted" />
        </div>

        <p v-else-if="attachments.length === 0" class="py-4 text-center text-sm text-muted">
            {{ $t('attachment.empty') }}
        </p>

        <ul v-else class="flex flex-col divide-y divide-default rounded-md ring ring-default">
            <li v-for="item in attachments" :key="item.id" class="flex items-center gap-3 px-3 py-2.5">
                <UIcon :name="fileIcon(item.content_type)" class="size-5 shrink-0 text-muted" />

                <div class="min-w-0 flex-1">
                    <!-- A plain link: the API reads the JWT cookie for it (docs/API.md → Auth). -->
                    <a
                        :href="`${API_BASE_URL}/attachments/${item.id}`"
                        class="block truncate text-sm font-medium text-highlighted hover:text-primary hover:underline"
                        :title="item.original_name"
                        download
                    >
                        {{ item.original_name }}
                    </a>
                    <p class="truncate text-xs text-dimmed">
                        {{ formatSize(item.size) }} · {{ item.uploaded_by?.name }} · {{ formatDateTime(item.uploaded_at) }}
                    </p>
                </div>

                <UButton
                    :to="`${API_BASE_URL}/attachments/${item.id}`"
                    external
                    download
                    icon="i-lucide-download"
                    color="neutral"
                    variant="ghost"
                    size="sm"
                    :aria-label="$t('attachment.download')"
                />
                <UButton
                    v-if="item.can_delete"
                    icon="i-lucide-trash-2"
                    color="error"
                    variant="ghost"
                    size="sm"
                    :aria-label="$t('general.delete')"
                    @click="remove(item)"
                />
            </li>
        </ul>
    </div>

    <AppConfirmDialog ref="confirmDialog" />
</template>

<script lang="ts" setup>
    import { onMounted, ref, useTemplateRef } from 'vue'
    import { useI18n } from 'vue-i18n'

    import AppConfirmDialog from '@/components/app/AppConfirmDialog.vue'
    import { TaskLimits } from '@/enums/task'
    import { formatDateTime } from '@/libs/dayjs'
    import { parseKnownError } from '@/libs/error'
    import { fileIcon } from '@/libs/file'
    import { formatSize } from '@/libs/number'
    import { PrintError, PrintSuccess } from '@/libs/toast'
    import { useAppStore } from '@/stores/app'
    import { API_BASE_URL, useFetchStore } from '@/stores/fetch'
    import type { IAttachment } from '@/types/Task'

    /*
     * Files on a task. An upload is multipart with one field, `file`, at most 25 MB — checked
     * here first, so a video from a phone is refused before it has been sent rather than after
     * (the server says the same with code 19). It goes through the fetch store like every other
     * call; the store sets no content type for a FormData body, and the browser writes the
     * multipart boundary itself.
     */
    const props = defineProps<{ taskId: string }>()

    const emit = defineEmits<((e: 'changed', count: number) => void)>()

    const { t }         = useI18n()
    const appStore      = useAppStore()
    const fetchStore    = useFetchStore()
    const confirmDialog = useTemplateRef<InstanceType<typeof AppConfirmDialog>>('confirmDialog')
    const fileInput     = useTemplateRef<HTMLInputElement>('fileInput')

    const attachments = ref<IAttachment[]>([])
    const loading     = ref(false)
    const loaded      = ref(false)
    const uploading   = ref(false)

    const load = async () => {
        loading.value = true

        const { statusCode, data } = await fetchStore.fetch(`/tasks/${props.taskId}/attachments`).get().json<IAttachment[]>()

        if (statusCode.value === 200) {
            attachments.value = data.value ?? []
            loaded.value      = true
        } else {
            PrintError(parseKnownError(data.value, t('attachment.load_failed')))
        }

        loading.value = false
    }

    const upload = async (file: File): Promise<boolean> => {
        if (file.size > TaskLimits.FileBytes) {
            PrintError(t('attachment.too_large_named', { name: file.name }))

            return false
        }

        const body = new FormData()
        body.append('file', file, file.name)

        const { statusCode, data } = await fetchStore.fetch(`/tasks/${props.taskId}/attachments`).post(body).json<{ id: string }>()

        if (statusCode.value === 201) return true

        PrintError(parseKnownError(data.value, t('attachment.upload_failed_named', { name: file.name })))

        return false
    }

    const onPicked = async (event: Event) => {
        const input = event.target as HTMLInputElement
        const files = Array.from(input.files ?? [])

        // The same file picked twice in a row must fire `change` again.
        input.value = ''

        if (files.length === 0) return

        uploading.value = true
        appStore.startLoading()

        let uploaded = 0

        for (const file of files) {
            if (await upload(file)) uploaded++
        }

        appStore.stopLoading()
        uploading.value = false

        if (uploaded > 0) {
            PrintSuccess(t('attachment.uploaded', uploaded))
            await load()
            emit('changed', attachments.value.length)
        }
    }

    const remove = async (item: IAttachment) => {
        const confirmed = await confirmDialog.value?.confirm(t('attachment.confirm_delete', { name: item.original_name }), {
            title:        t('attachment.delete'),
            confirmLabel: t('general.delete'),
        })
        if (!confirmed) return

        appStore.startLoading()

        const { statusCode, data } = await fetchStore.fetch(`/attachments/${item.id}`).delete().json()

        appStore.stopLoading()

        if (statusCode.value === 204) {
            PrintSuccess(t('attachment.deleted'))
            await load()
            emit('changed', attachments.value.length)
        } else {
            PrintError(parseKnownError(data.value, t('attachment.delete_failed')))
        }
    }

    onMounted(load)
</script>
